import React, { useEffect } from 'react';
import { Menu, Users, ChevronUp, ChevronDown, Volume2, MicOff, Video, Monitor, Hash, Film, X, Gamepad2, UserRound } from 'lucide-react';
import FriendsPanel from './FriendsPanel';
import { useFriends } from '../../hooks/useFriends';
import { preloadImage } from '../../services/imageCache';

import { useEntity } from '../../core/resolvers/useEntity';
import { Channel } from '../../core/resolvers/EntityResolver';
import { UserBadge } from '../../core/identity/IdentityRenderer';
import { entityResolver } from '../../core/resolvers/EntityResolver';
import { useUIStore } from '../../stores/uiStore';
import { PermissionService, usePermission } from '../../services/PermissionService';
import { PERMISSION_MANAGE_SERVER, PERMISSION_EDIT_CHANNELS } from '../../core/permissions/permissions';

interface ContextPanelProps {
  mobileView: 'sidebar' | 'chat';
  isSidebarCollapsed: boolean;
  isCinemaMode: boolean;
  activeTab: 'chat' | 'feed';
  setActiveTab: (tab: 'chat' | 'feed') => void;
  setMobileView: (view: 'sidebar' | 'chat') => void;
  activeVault: any;
  sidebarView: 'dms' | 'channels';
  setSidebarView: (view: 'dms' | 'channels') => void;
  showVaultDropdown: boolean;
  setShowVaultDropdown: (show: boolean) => void;
  currentUser: any;
  adminModeEnabled: boolean;
  setContextMenu: (state: any) => void;
  channels: Channel[];
  categories?: any[];
  voiceParticipants: Record<string, any[]>;
  isMuted: boolean;
  isVideoOn: boolean;
  isScreenSharing: boolean;
  peerVoiceStates: Record<string, { isMuted: boolean; isCameraOn: boolean; isScreenSharing: boolean }>;
  activeSpeakerId: string;
  getUserFontStyle: (userId: string | undefined) => React.CSSProperties;
  playSound: (type: 'join' | 'leave' | 'mute' | 'unmute' | 'toggle' | 'message') => void;
  setActiveChannelId: (id: string | null) => void;
  setCallChannelId: (id: string | null) => void;
  setCallVaultId: (id: string | null) => void;
  setIsCallActive: (active: boolean) => void;
  handleChannelContextMenu: (e: React.MouseEvent, channelId: string, channelName?: string) => void;
  handleSidebarAreaContextMenu: (e: React.MouseEvent) => void;
  activeChannelId: string | null;
  isCallActive: boolean;
  setShowSideChat: (show: boolean) => void;
  contacts: any[];
  contactsLoading?: boolean;
  dmRooms: any[];
  activeContactId: string | null;
  setActiveContactId: (id: string | null) => void;
  setActiveVault: (vault: any) => void;
  handleUserContextMenu: (e: React.MouseEvent, userId: string) => void;
  isTopBarPinned: boolean;
  setIsTopBarPinned: (pinned: boolean) => void;
  isTopBarExpanded: boolean;
  setIsTopBarExpanded: (expanded: boolean) => void;
  feedFilter?: 'all' | 'following' | 'expiring';
  setFeedFilter?: (filter: 'all' | 'following' | 'expiring') => void;
  getUserRoleColor?: (userId: string) => string | undefined;
  onReorderChannels?: (channels: Channel[]) => void;
  hiddenDmUserIds?: string[];
  onHideDM?: (userId: string) => void;
  children?: React.ReactNode;
}

import { FONT_FAMILIES, getNameStyle } from '../../core/identity/styles';

const ChannelNameRenderer = ({ channel }: { channel: Channel }) => {
  const { entity } = useEntity(channel.id, 'channel');
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(channel.name);
  const name = isUuid && entity ? entity.name : channel.name;
  
  const cust = channel.customization || {};
  const font = cust.font || cust.nameFont;
  const effect = cust.nameStyle || cust.nameEffect;
  const c1 = cust.color1 || cust.nameEffectColor1 || cust.nameColor;
  const c2 = cust.color2 || cust.nameEffectColor2;

  return (
    <span
      style={{
        fontFamily: FONT_FAMILIES[font as keyof typeof FONT_FAMILIES] || 'inherit',
        color: cust.nameColor || 'inherit', // Fallback for pure solid coloring if needed
        ...getNameStyle(effect, c1, c2, cust.gradientDirection)
      }}
    >
      {name}
    </span>
  );
};

const CategoryNameRenderer = ({ category }: { category: any }) => {
  const cust = category.customization || {};
  const font = cust.font || cust.nameFont;
  const effect = cust.nameStyle || cust.nameEffect;
  const c1 = cust.color1 || cust.nameEffectColor1 || cust.nameColor;
  const c2 = cust.color2 || cust.nameEffectColor2;

  return (
    <span
      style={{
        fontFamily: FONT_FAMILIES[font as keyof typeof FONT_FAMILIES] || 'inherit',
        color: cust.nameColor || 'inherit',
        ...getNameStyle(effect, c1, c2, cust.gradientDirection)
      }}
    >
      {category.name}
    </span>
  );
};

const VaultNameRenderer = ({ vault }: { vault: any }) => {
  const cust = vault.customization || {};
  const font = cust.font || cust.nameFont;
  const effect = cust.nameStyle || cust.nameEffect;
  const c1 = cust.color1 || cust.nameEffectColor1 || cust.nameColor;
  const c2 = cust.color2 || cust.nameEffectColor2;

  return (
    <span
      style={{
        fontFamily: FONT_FAMILIES[font as keyof typeof FONT_FAMILIES] || 'inherit',
        color: cust.nameColor || 'inherit',
        ...getNameStyle(effect, c1, c2, cust.gradientDirection)
      }}
    >
      {vault.name}
    </span>
  );
};

export default function ContextPanel({
  mobileView,
  isSidebarCollapsed,
  isCinemaMode,
  activeTab,
  setActiveTab,
  setMobileView,
  activeVault,
  sidebarView,
  setSidebarView,
  showVaultDropdown,
  setShowVaultDropdown,
  currentUser,
  adminModeEnabled,
  setContextMenu,
  channels,
  categories = [],
  voiceParticipants,
  isMuted,
  isVideoOn,
  isScreenSharing,
  peerVoiceStates,
  activeSpeakerId,
  getUserFontStyle,
  playSound,
  setActiveChannelId,
  setCallChannelId,
  setCallVaultId,
  setIsCallActive,
  handleChannelContextMenu,
  handleSidebarAreaContextMenu,
  activeChannelId,
  isCallActive,
  setShowSideChat,
  contacts,
  contactsLoading,
  dmRooms,
  activeContactId,
  setActiveContactId,
  setActiveVault,
  handleUserContextMenu,
  isTopBarPinned,
  setIsTopBarPinned,
  isTopBarExpanded,
  setIsTopBarExpanded,
  feedFilter = 'all',
  setFeedFilter,
  getUserRoleColor,
  onReorderChannels,
  hiddenDmUserIds = [],
  onHideDM,
  children,
}: ContextPanelProps) {
  const [draggedChannelIndex, setDraggedChannelIndex] = React.useState<number | null>(null);
  const [dragOverChannelIndex, setDragOverChannelIndex] = React.useState<number | null>(null);
  const [collapsedCategories, setCollapsedCategories] = React.useState<Record<string, boolean>>({});

  // Friends view swaps in place of the DM list via the header toggle. Kept
  // local to the sidebar so opening it never changes the main chat panel.
  const [showFriends, setShowFriends] = React.useState(false);
  const {
    friends: friendList,
    incoming: friendRequests,
    outgoing: outgoingRequests,
    loading: friendsLoading,
    sendRequestByUsername,
    acceptRequest,
    removeFriend,
  } = useFriends(currentUser?.id);

  const toggleCategory = (categoryId: string) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const groupedChannels = React.useMemo(() => {
    const groups: { category: any | null, channels: Channel[] }[] = [];
    const uncategorized = channels.filter(c => !c.category_id);
    if (uncategorized.length > 0) {
      groups.push({ category: null, channels: uncategorized });
    }
    categories.forEach(cat => {
      groups.push({ category: cat, channels: channels.filter(c => c.category_id === cat.id) });
    });
    return groups;
  }, [channels, categories]);

  const sortedContacts = React.useMemo(() => {
    return [...contacts].sort((a, b) => {
      const roomA = dmRooms.find(r => r.members?.includes(a.id));
      const roomB = dmRooms.find(r => r.members?.includes(b.id));
      const dateA = roomA?.last_message_at ? new Date(roomA.last_message_at).getTime() : 0;
      const dateB = roomB?.last_message_at ? new Date(roomB.last_message_at).getTime() : 0;
      if (dateA === dateB) return a.name.localeCompare(b.name);
      return dateB - dateA;
    });
  }, [contacts, dmRooms]);

  const visibleContacts = React.useMemo(() => {
    return sortedContacts.filter(c => !hiddenDmUserIds.includes(c.id) || c.id === activeContactId);
  }, [sortedContacts, hiddenDmUserIds, activeContactId]);

  const hasManagePermissions = usePermission(PERMISSION_EDIT_CHANNELS, { vaultId: activeVault?.id });

  // Preload the server banner into the image cache so vault switches are instant.
  useEffect(() => {
    const bannerUrl = activeVault?.customization?.bannerImage;
    if (bannerUrl) preloadImage(bannerUrl);
  }, [activeVault?.customization?.bannerImage]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedChannelIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedChannelIndex !== null && draggedChannelIndex !== index) {
      setDragOverChannelIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverChannelIndex(null);
  };

  const handleDrop = (e: React.DragEvent, index: number, targetCategoryId?: string | null) => {
    e.preventDefault();
    if (draggedChannelIndex !== null && draggedChannelIndex !== index) {
      const newChannels = [...channels];
      const [draggedItem] = newChannels.splice(draggedChannelIndex, 1);
      
      if (targetCategoryId !== undefined) {
        draggedItem.category_id = targetCategoryId;
      }
      
      newChannels.splice(index, 0, draggedItem);
      onReorderChannels?.(newChannels);
    }
    setDraggedChannelIndex(null);
    setDragOverChannelIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedChannelIndex(null);
    setDragOverChannelIndex(null);
  };

  return (
    <aside className={`sub-sidebar ${mobileView === 'sidebar' ? 'flex' : 'hidden md:flex'} ${isSidebarCollapsed || isCinemaMode ? 'hidden w-0 overflow-hidden' : 'w-full md:w-80'} flex-col flex-shrink-0`}>
      <div className="flex flex-col h-full bg-black/20">


        <div className="flex-1 overflow-hidden flex flex-col">
          {(sidebarView === 'channels' && activeVault) ? (
            <>
              <button 
                onClick={() => setShowVaultDropdown(!showVaultDropdown)} 
                onContextMenu={async (e) => {
                  if (!activeVault || !currentUser) return;
                  const canManageVault = await PermissionService.has(currentUser, PERMISSION_MANAGE_SERVER, { vaultId: activeVault.id });
                  if (!canManageVault) return;
                  e.preventDefault();
                  setContextMenu({
                    x: e.clientX,
                    y: e.clientY,
                    type: 'vault-header',
                    targetId: activeVault.id,
                    visible: true
                  });
                }}
                className={`w-full flex justify-between hover:bg-white/5 transition-all group relative overflow-hidden shrink-0 h-32 px-4 pb-3 pt-4 items-end shadow-lg isolate`}
              >
                <img
                  src={activeVault.customization?.bannerImage || '/default-server-banner.gif'}
                  className="absolute inset-0 w-full h-full object-cover z-0"
                  style={{ objectPosition: activeVault.customization?.bannerPosition || '50% 50%' }}
                  alt="Server Banner"
                />
                <div className="absolute bottom-0 w-full h-1/2 bg-gradient-to-t from-black/50 to-transparent z-0" />
                <div className="flex items-center gap-3 relative z-10">
                  <h2 className="font-black text-[11px] uppercase tracking-widest truncate max-w-[160px] text-white drop-shadow-md"><VaultNameRenderer vault={activeVault} /></h2>
                </div>
                <ChevronUp size={14} className={`transition-transform duration-300 relative z-10 ${showVaultDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-1.5 no-scrollbar" onContextMenu={handleSidebarAreaContextMenu}>
                {groupedChannels.map((group) => (
                  <div key={group.category?.id || 'uncategorized'} className="mb-2">
                    {group.category && (
                      <button
                        onClick={() => toggleCategory(group.category.id)}
                        onContextMenu={(e) => {
                           e.preventDefault();
                           setContextMenu({ x: e.clientX, y: e.clientY, type: 'category', targetId: group.category.id, targetName: group.category.name, visible: true });
                        }}
                        onDragOver={(e) => {
                          if (hasManagePermissions && draggedChannelIndex !== null) {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                          }
                        }}
                        onDrop={(e) => {
                          if (hasManagePermissions && draggedChannelIndex !== null) {
                            e.preventDefault();
                            const newChannels = [...channels];
                            const [draggedItem] = newChannels.splice(draggedChannelIndex, 1);
                            draggedItem.category_id = group.category.id;
                            newChannels.push(draggedItem);
                            onReorderChannels?.(newChannels);
                            setDraggedChannelIndex(null);
                            setDragOverChannelIndex(null);
                          }
                        }}
                        className={`w-full flex items-center gap-1 px-2 mb-1 group/cat transition-colors ${draggedChannelIndex !== null && draggedChannelIndex > 0 ? 'bg-[var(--neon)]/20' : ''}`}
                      >
                        <ChevronDown size={10} className={`text-white/40 transition-transform ${collapsedCategories[group.category.id] ? '-rotate-90' : ''}`} />
                        <span className="text-[10px] font-black text-white/40 uppercase tracking-widest"><CategoryNameRenderer category={group.category} /></span>
                      </button>
                    )}
                    
                    {!collapsedCategories[group.category?.id || 'uncategorized'] && (
                      <div className="flex flex-col gap-0.5">
                        {group.channels.map((channel) => {
                          const index = channels.findIndex(c => c.id === channel.id);
                          const isDragged = draggedChannelIndex === index;
                          const isDragOver = dragOverChannelIndex === index;
                          
                          return (
                            <div
                              key={channel.id}
                              draggable={true}
                              onDragStart={(e) => handleDragStart(e, index)}
                              onDragOver={(e) => handleDragOver(e, index)}
                              onDragLeave={(e) => handleDragLeave()}
                              onDrop={(e) => handleDrop(e, index, group.category?.id || null)}
                              onDragEnd={(e) => handleDragEnd()}
                              className={`flex flex-col gap-0.5 transition-all cursor-grab active:cursor-grabbing ${isDragged ? 'opacity-50' : ''} ${isDragOver && draggedChannelIndex !== null && draggedChannelIndex < index ? 'border-b-2 border-[var(--neon)]' : ''} ${isDragOver && draggedChannelIndex !== null && draggedChannelIndex > index ? 'border-t-2 border-[var(--neon)]' : ''}`}
                            >
                              {channel.type === 'voice' ? (
                        <>
                          <button
                            className={`channel-item vc flex items-center gap-3 px-3 py-2 rounded-xl transition-all cursor-grab active:cursor-grabbing ${activeChannelId === channel.id ? 'active' : ''}`}
                            onClick={() => {
                              playSound('join');
                              setActiveChannelId(channel.id);
                              setCallChannelId(channel.id);
                              setCallVaultId(activeVault?.id || null);
                              setIsCallActive(true);
                              setActiveTab('chat');
                              setMobileView('chat');
                            }}
                            onContextMenu={(e) => handleChannelContextMenu(e, channel.id, channel.name)}
                          >
                            <div className="p-1.5 rounded-lg bg-[var(--neon)]/10 text-[var(--neon)]"><Volume2 size={14} /></div>
                            <span className="flex-1 text-left text-xs font-bold">
                              <ChannelNameRenderer channel={channel} />
                            </span>
                          </button>
                          {voiceParticipants[channel.id]?.map((p: any) => {
                            const isMe = p.id === currentUser?.id;
                            const pState = isMe
                              ? { isMuted, isCameraOn: isVideoOn, isScreenSharing }
                              : (peerVoiceStates[p.id] || { isMuted: false, isCameraOn: false, isScreenSharing: false });
                            const isSpeaking = activeSpeakerId === p.id;
                            return (
                              <div 
                                key={p.id} 
                                className="flex items-center gap-2 px-8 py-1 cursor-pointer hover:bg-white/5 transition-colors rounded-lg mx-2" 
                                onClick={() => useUIStore.getState().setViewingProfileId(p.id)}
                                onContextMenu={(e) => handleUserContextMenu(e, p.id)}
                              >
                                <div className="relative shrink-0">
                                  <img src={p.avatar} className={`w-5 h-5 rounded-full object-cover border transition-all duration-300 ${isSpeaking ? 'border-emerald-400 scale-105 shadow-[0_0_8px_rgba(74,222,128,0.5)]' : 'border-white/10'}`} alt="" />
                                  {isSpeaking && (
                                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-500 rounded-full border border-black animate-pulse" />
                                  )}
                                </div>
                                <span 
                                  className={`text-[10px] font-bold flex-1 truncate transition-colors duration-300 ${isSpeaking ? 'text-emerald-400' : 'text-white/70'}`} 
                                  style={{ 
                                    ...getUserFontStyle(p.id), 
                                    color: isSpeaking ? undefined : (getUserRoleColor ? getUserRoleColor(p.id) : undefined) 
                                  }}
                                >
                                  {p.name}
                                </span>
                                <div className="flex items-center gap-1 shrink-0">
                                  {p.currentGame && (
                                    <span title={`Active Activity: ${p.currentGame}`} aria-label={`Active Activity: ${p.currentGame}`} role="img" className="animate-pulse">
                                      <Gamepad2 size={11} aria-hidden="true" className="text-emerald-400 drop-shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                                    </span>
                                  )}

                                  {pState.isMuted && <span title="Muted" aria-label="Muted" role="img"><MicOff size={10} aria-hidden="true" className="text-red-400" /></span>}
                                  {pState.isCameraOn && <span title="Camera On" aria-label="Camera on" role="img"><Video size={10} aria-hidden="true" className="text-[var(--neon)]" /></span>}
                                  {pState.isScreenSharing && <span title="Screen Sharing" aria-label="Screen sharing" role="img"><Monitor size={10} aria-hidden="true" className="text-[var(--neon)]" /></span>}
                                  {p.isWatchTogetherHost && <span title="Hosting Watch Together" aria-label="Hosting Watch Together" role="img"><Film size={10} aria-hidden="true" className="text-[var(--neon)]" /></span>}
                                </div>

                              </div>
                            );
                          })}
                        </>
                      ) : (
                        <button
                          className={`channel-item flex items-center gap-3 px-3 py-2 rounded-xl transition-all cursor-grab active:cursor-grabbing ${activeChannelId === channel.id ? 'active' : 'text-white/40 hover:text-white'}`}
                          onClick={() => {
                            setActiveChannelId(channel.id);
                            setActiveTab('chat');
                            setMobileView('chat');
                            if (isCallActive) setShowSideChat(true);
                          }}
                          onContextMenu={(e) => handleChannelContextMenu(e, channel.id, channel.name)}
                        >
                          <Hash size={14} /> 
                          <span className="text-xs font-bold">
                            <ChannelNameRenderer channel={channel} />
                          </span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            </div>
          ))}
        </div>
      </>
    ) : (
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="px-5 pt-3 pb-1 flex items-center justify-between shrink-0">
                <span className="text-[9px] font-black text-white/20 uppercase tracking-[0.2em]">
                  {showFriends ? 'Friends' : 'Direct Messages'}
                </span>
                <div className="flex items-center gap-2">
                  {/* Swaps the list below between DMs and Friends. The dot
                      turns into a count chip when requests are waiting, so an
                      inbound request is visible without opening the view. */}
                  <button
                    onClick={() => setShowFriends(v => !v)}
                    title={showFriends ? 'Show direct messages' : 'Show friends'}
                    className={`relative p-1 rounded-lg border transition-all ${
                      showFriends
                        ? 'bg-[var(--neon)]/20 border-[var(--neon)] text-[var(--neon)]'
                        : friendRequests.length > 0
                          ? 'bg-[var(--neon)]/10 border-[var(--neon)]/50 text-[var(--neon)] animate-pulse'
                          : 'bg-white/5 border-white/10 text-white/40 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <UserRound size={12} />
                    {friendRequests.length > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-1 rounded-full bg-[var(--neon)] text-black text-[8px] font-black flex items-center justify-center border border-black/40">
                        {friendRequests.length > 9 ? '9+' : friendRequests.length}
                      </span>
                    )}
                  </button>
                  {!showFriends && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
                </div>
              </div>

              {showFriends ? (
                <FriendsPanel
                  friends={friendList}
                  incoming={friendRequests}
                  outgoing={outgoingRequests}
                  loading={friendsLoading}
                  onAccept={acceptRequest}
                  onRemove={removeFriend}
                  onAddByUsername={sendRequestByUsername}
                  onOpenDM={(userId) => {
                    setActiveVault(null);
                    setActiveContactId(userId);
                    setActiveTab('chat');
                    setMobileView('chat');
                    if (isCallActive) setShowSideChat(true);
                  }}
                  onUserContextMenu={handleUserContextMenu}
                />
              ) : (
            <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2 no-scrollbar">
              {contactsLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-2xl">
                    <div className="w-10 h-10 rounded-xl bg-white/5 animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-white/10 rounded animate-pulse w-24" />
                      <div className="h-2.5 bg-white/5 rounded animate-pulse w-32" />
                    </div>
                  </div>
                ))
              ) : (
                visibleContacts.map(c => (
                  <div key={c.id} className="relative group">
                    <button 
                      className={`dm-item w-full flex items-center gap-3 p-2 rounded-2xl transition-all ${activeContactId === c.id && !activeVault ? 'bg-white/10' : 'hover:bg-white/5'}`} 
                      onClick={() => { 
                        setActiveVault(null); 
                        setActiveContactId(c.id); 
                        setActiveTab('chat'); 
                        setMobileView('chat'); 
                        if (isCallActive) setShowSideChat(true); 
                      }} 
                      onContextMenu={(e) => handleUserContextMenu(e, c.id)}
                    >
                      <div className="relative">
                        <img src={c.avatar} className="w-10 h-10 rounded-xl object-cover border border-white/5 group-hover:border-[var(--neon)]/40 transition-colors" alt="" />
                        {c.isOnline && (
                          <span className={`online-dot -bottom-1 -right-1 ${
                            c.presence === 'Idle' ? 'is-idle animate-pulse' :
                            c.presence === 'Away' ? 'is-away' : ''
                          }`}></span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 text-left flex flex-col justify-center">
                        <div className="flex items-center gap-2">
                          <div 
                            className="text-xs font-black text-white group-hover:text-[var(--neon)] transition-colors truncate" 
                            style={{ 
                              ...getUserFontStyle(c.id), 
                              color: getUserRoleColor ? getUserRoleColor(c.id) : undefined 
                            }}
                          >
                            {c.name}
                          </div>
                          <UserBadge customization={entityResolver.resolveUser(c.id)?.customization} />
                        </div>
                        <div className="text-[10px] text-white/30 truncate uppercase tracking-tighter">{c.presence || c.status}</div>
                      </div>
                    </button>
                    {onHideDM && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onHideDM(c.id);
                        }}
                        title="Hide DM"
                        className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1.5 text-white/40 hover:text-amber-400 hover:bg-white/10 rounded-lg transition-all z-10"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
              )}
            </div>
          )}
        </div>

        {children}
      </div>
    </aside>
  );
}
